import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const ShirtIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M16 3l-4 3-4-3-5 4 2 4 2-1v11h10V10l2 1 2-4-5-4z" />
  </svg>
);

const CheckIcon = () => (
  <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

const InfoIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
  </svg>
);

const UniformPolicyPage = () => {
  const tops = [
    'White or navy polo shirt (short or long sleeve)',
    'PS 96 spirit shirt on designated days',
    'Navy or white cardigan or sweater in cooler months',
  ];

  const bottoms = [
    'Navy blue or khaki pants, shorts, skirts, or skorts',
    'Bottoms should fit at the waist — no sagging or excessively tight fits',
    'Shorts and skirts should reach mid-thigh or longer',
  ];

  const shoes = [
    'Closed-toe, rubber-soled shoes safe for stairs and gym',
    'Sneakers required on Physical Education days',
    'No open-toed shoes, sandals, or heels',
  ];

  const notes = [
    'Hooded sweatshirts may be worn over uniform but hoods should stay down inside the building',
    'Jewelry should be simple and safe for play and PE',
    'Head coverings worn for religious reasons are always welcome',
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <main id="main-content" className="pt-0">
        {/* Hero */}
        <section
          className="relative pt-36 pb-20 bg-[#00213a] overflow-hidden"
        >
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0"
            style={{ height: '4rem', background: 'linear-gradient(to bottom, rgba(0,33,58,0) 0%, rgba(255,255,255,1) 100%)' }}
            aria-hidden="true"
          />

          <div className="max-w-6xl mx-auto px-6 lg:px-8 text-center">
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20">
                <ShirtIcon />
                <span className="text-sm font-semibold text-white tracking-tight">
                  School Policy
                </span>
              </div>
            </div>
            <h1
              className="text-5xl lg:text-7xl font-semibold text-white mb-6 tracking-tight"
              style={{ letterSpacing: '-0.02em' }}
            >
              Uniform Policy
            </h1>
            <p className="text-2xl text-white/90 font-normal max-w-3xl leading-relaxed mx-auto">
              Our uniform helps students feel proud, focused, and part of the
              PS 96 Lions community.
            </p>
          </div>
        </section>

        {/* Overview note */}
        <section className="max-w-5xl mx-auto px-6 lg:px-8 mt-12 mb-20">
          <div className="bg-[#fdbb2e]/10 border border-[#fdbb2e]/30 rounded-xl p-6 flex gap-4">
            <div className="text-[#00213a] mt-0.5">
              <InfoIcon />
            </div>
            <div className="text-gray-800 text-sm leading-relaxed">
              <strong className="text-[#00213a]">Please note:</strong> The
              guidance below reflects our standard uniform expectations. For
              the official PS 96 uniform policy document, please contact the
              main office or your child's teacher. Specific approved vendors
              and spirit-wear order forms are shared each school year.
            </div>
          </div>
        </section>

        {/* Sections */}
        <section className="max-w-7xl mx-auto px-6 lg:px-8 pb-20">
          <div className="grid md:grid-cols-2 gap-6">
            <UniformSection title="Shirts &amp; Tops" items={tops} />
            <UniformSection title="Pants, Skirts &amp; Shorts" items={bottoms} />
            <UniformSection title="Footwear" items={shoes} />
            <UniformSection title="Accessories &amp; Notes" items={notes} />
          </div>
        </section>

        {/* Spirit day */}
        <section className="bg-gray-50 py-20">
          <div className="max-w-5xl mx-auto px-6 lg:px-8">
            <div className="bg-white border border-gray-200 rounded-xl p-8 lg:p-12">
              <h2
                className="text-3xl font-semibold text-gray-900 mb-4 tracking-tight"
                style={{ letterSpacing: '-0.02em' }}
              >
                School Spirit Days
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                On Fridays and for special assemblies or community events,
                students are encouraged to wear their official PS 96 Lions
                spirit shirt with uniform bottoms.
              </p>
              <p className="text-gray-600 text-sm">
                Families will be notified in advance of field trips, grade-level
                events, and theme days that may adjust uniform expectations.
              </p>
            </div>
          </div>
        </section>

        {/* Questions */}
        <section className="bg-[#00213a] py-16">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2
              className="text-3xl font-semibold text-white mb-4 tracking-tight"
              style={{ letterSpacing: '-0.02em' }}
            >
              Questions About the Uniform?
            </h2>
            <p className="text-white/90 mb-8">
              The main office is happy to help with vendor recommendations,
              financial assistance resources, and any accommodations your
              family may need.
            </p>
            <a
              href="tel:+12128605851"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white text-[#00213a] font-semibold rounded-lg hover:bg-gray-50 transition-all duration-200"
            >
              Call the Main Office · (212) 860-5851
            </a>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

const UniformSection = ({ title, items }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-6 lg:p-8 hover:shadow-md transition-all duration-200">
    <div className="flex items-center gap-3 mb-5">
      <div className="w-10 h-10 rounded-lg bg-[#fdbb2e]/10 flex items-center justify-center text-[#00213a]">
        <ShirtIcon />
      </div>
      <h2
        className="text-xl font-semibold text-gray-900 tracking-tight"
        style={{ letterSpacing: '-0.01em' }}
        dangerouslySetInnerHTML={{ __html: title }}
      />
    </div>
    <ul className="space-y-3">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-3 text-gray-700">
          <div className="text-[#00213a] mt-0.5">
            <CheckIcon />
          </div>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  </div>
);

export default UniformPolicyPage;
